import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { IconFilter, IconRefresh } from '@tabler/icons-react';
import MostSearchedDrugs from './MostSearchedDrugs';
import DrugSearchStatsCards from './DrugSearchStatsCards';
import DrugSearchToCoverageInsightsCard from './DrugSearchToCoverageInsights';
import superAdminApiService from '../../../services/super-admin-api.service';
import MostActiveDrugSearchUsers from './MostActiveDrugSearchUsers';

const DrugSearchModuleUsage = () => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [filters, setFilters] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState(null);

  const baseRoles = [
    { id: 'all', name: 'All Roles' },
    { id: 'provider', name: 'Provider' },
    { id: 'support', name: 'Support Staff' },
  ];

  useEffect(() => {
    const fetchOrganizations = async () => {
      setOrgError(false);
      setOrgLoading(true);

      try {
        const data = await superAdminApiService.listOrganizationUsers();
        const filteredData = data.results.map((organization) => {
          return {
            id: organization?.organization_id,
            name: organization?.org_name,
          };
        });
        setOrganizations([
          { id: 'all', name: 'All Organizations' },
          ...filteredData,
        ]);
      } catch (err) {
        console.error('Failed to load organizations:', err);
        setOrgError('Failed to load organizations');
      } finally {
        setOrgLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  const applyFilters = () => {
    const [start, end] = dateRange;

    const newFilters = {
      ...(selectedRole !== 'all' && { userRole: selectedRole }),
      ...(selectedOrg !== 'all' && { organization: selectedOrg }),
      ...(start &&
        end && {
          dateRange: {
            start: start.toLocaleDateString('en-CA'),
            end: end.toLocaleDateString('en-CA'),
          },
        }),
    };

    setFilters(() => newFilters);
  };

  const resetFilters = () => {
    setDateRange([null, null]);
    setSelectedRole('all');
    setSelectedOrg('all');
    setFilters({});
  };

  const getFilterSummary = () => {
    const [start, end] = dateRange;
    const parts = [];

    if (start && end) {
      parts.push(
        `Date: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
      );
    }

    if (selectedRole !== 'all') {
      const roleName = baseRoles.find((r) => r.id === selectedRole)?.name;
      parts.push(`Role: ${roleName}`);
    }

    if (selectedOrg !== 'all') {
      const orgName = organizations.find((o) => o.id === selectedOrg)?.name;
      parts.push(`Organization: ${orgName}`);
    }

    return parts.length ? parts.join(' | ') : '';
  };

  return (
    <div className="innerpage detailed-page">
      <div className="w-full">
        {/* Header and filter summary */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-0">Drug Search Module Usage</h4>
          <p className="text-muted-foreground text-sm mb-0">
            Analytics on drug search activity and user engagement patterns
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="bg-white py-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <IconFilter className="mr-2" size={18} />
                <span className="font-medium">Filters</span>
                {(dateRange[0] ||
                  selectedRole !== 'all' ||
                  selectedOrg !== 'all') && (
                  <span className="ml-3 text-muted-foreground text-sm">
                    {getFilterSummary()}
                  </span>
                )}
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={resetFilters}
                title="Reset Filters"
              >
                <IconRefresh size={16} className="mr-1" />
                Reset
              </Button>
            </div>
          </CardHeader>

          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Date Range */}
              <div className="space-y-2">
                <Label>Date Range</Label>
                <DatePicker
                  selectsRange
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  onChange={setDateRange}
                  isClearable
                  placeholderText="Select date range"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  dateFormat="MMM d, yyyy"
                />
              </div>

              {/* Role */}
              <div className="space-y-2">
                <Label>User Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {baseRoles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Organization */}
              <div className="space-y-2">
                <Label>Organization</Label>
                {orgLoading ? (
                  <div className="text-muted-foreground text-sm">Loading...</div>
                ) : orgError ? (
                  <div className="text-red-600 text-sm">{orgError}</div>
                ) : (
                  <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </CardContent>

          <CardFooter className="bg-white py-2">
            <div className="flex justify-end w-full">
              <Button variant="outline" onClick={applyFilters}>
                Apply Filters
              </Button>
            </div>
          </CardFooter>
        </Card>

        {/* Analytics Sections */}
        <DrugSearchStatsCards filters={filters} />

        <div className="mb-6">
          <Card className="shadow-sm border-0">
            <CardHeader>
              <CardTitle className="text-base font-bold">
                Most Active Drug Search Users
              </CardTitle>
              <CardDescription className="text-sm">
                Users with the most frequent drug search activity
              </CardDescription>
            </CardHeader>
            <CardContent>
              <MostActiveDrugSearchUsers filters={filters} />
            </CardContent>
          </Card>
        </div>

        <div className="mb-6">
          <Card className="shadow-sm border-0">
            <CardHeader>
              <CardTitle className="text-base font-bold">Top 10 Most Searched Drugs</CardTitle>
              <CardDescription className="text-sm">
                Frequently searched medications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <MostSearchedDrugs filters={filters} />
            </CardContent>
          </Card>
        </div>

        <div className="mb-6">
          <DrugSearchToCoverageInsightsCard filters={filters} />
        </div>
      </div>
    </div>
  );
};

export default DrugSearchModuleUsage;
